import clsx from 'clsx'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft,
  CheckCircle2,
  Clock3,
  Film,
  Loader2,
  MessageSquare,
  RotateCcw,
  XCircle,
} from 'lucide-react'

import {
  approveAsset,
  approveShot,
  createAssetComment,
  createShotComment,
  listAssetComments,
  listReviewQueue,
  listShotComments,
  requestAssetChanges,
  requestShotChanges,
  resolveAssetComment,
  resolveComment,
  type ReviewComment,
  type ReviewQueueItem,
  type ReviewSubjectFilter,
} from '@/api/studio-production'
import { notify } from '@/shared/lib/notify'
import { SegmentedTabs } from '@/shared/components/ui/tabs'
import { ProtoBadge, ProtoBtn, ProtoInput, ProtoTextarea } from '@/features/studio/components/ui-kit'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { productionBasePath } from '@/features/studio/lib/production-path'
import { resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'
import { ReviewItemContextBar } from '@/features/studio/pages/reviews/ReviewItemContextBar'
import { ReviewMediaPreview, ReviewQueueThumb, reviewMediaKind } from '@/features/studio/pages/reviews/review-media'
import type { ReviewVideoPlayerHandle } from '@/features/studio/pages/reviews/ReviewVideoPlayer'
import { formatReviewTimecode, formatReviewTimecodeRange } from '@/features/studio/pages/reviews/review-timecode'
import {
  filterReviewQueue,
  isReviewAssetItem,
  isReviewShotItem,
  reviewItemKey,
  reviewItemSubjectType,
  reviewItemTitle,
  reviewStatusTone,
} from '@/features/studio/pages/reviews/review-utils'
import '@/features/studio/styles/review-workspace.css'

const FILTER_OPTIONS: ReviewSubjectFilter[] = [
  'all',
  'shot',
  'character',
  'location',
  'prop',
  'costume',
  'style',
]

export function ReviewsPage() {
  const { productionId = '' } = useParams()
  const [searchParams] = useSearchParams()
  const { t } = useTranslation()
  const base = productionBasePath(productionId)
  const [queue, setQueue] = useState<ReviewQueueItem[]>([])
  const [filter, setFilter] = useState<ReviewSubjectFilter>('all')
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [comments, setComments] = useState<ReviewComment[]>([])
  const [commentBody, setCommentBody] = useState('')
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false)
  const [rejectFeedback, setRejectFeedback] = useState('')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [inOutRange, setInOutRange] = useState<{ in: number | null; out: number | null }>({
    in: null,
    out: null,
  })
  const [focusCommentId, setFocusCommentId] = useState<string | null>(null)
  const pendingSeekMsRef = useRef<number | null>(null)
  const videoPlayerRef = useRef<ReviewVideoPlayerHandle>(null)

  const filteredQueue = useMemo(() => filterReviewQueue(queue ?? [], filter), [queue, filter])

  const reloadQueue = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await listReviewQueue(productionId)
      const items = res.items ?? []
      setQueue(items)
      setSelectedKey((prev) => {
        if (prev && items.some((item) => reviewItemKey(item) === prev)) return prev
        const first = items[0]
        return first ? reviewItemKey(first) : null
      })
      if (items.length === 0) {
        setComments([])
      }
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'reviews.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [productionId])

  const reloadComments = useCallback(
    async (item: ReviewQueueItem) => {
      try {
        if (isReviewShotItem(item)) {
          const res = await listShotComments(productionId, item.public_id)
          setComments(res.items ?? [])
          return
        }
        if (isReviewAssetItem(item) && reviewMediaKind(item) === 'video') {
          const res = await listAssetComments(productionId, item.public_id)
          setComments(res.items ?? [])
          return
        }
        setComments([])
      } catch (e) {
        setError(resolveApiErrorMessage(e, 'reviews.loadFailed'))
      }
    },
    [productionId],
  )

  useEffect(() => {
    void reloadQueue()
  }, [reloadQueue])

  const selected = useMemo(
    () => (queue ?? []).find((q) => reviewItemKey(q) === selectedKey) ?? null,
    [queue, selectedKey],
  )

  useEffect(() => {
    if (
      selected &&
      (isReviewShotItem(selected) || (isReviewAssetItem(selected) && reviewMediaKind(selected) === 'video'))
    ) {
      void reloadComments(selected)
    } else {
      setComments([])
    }
    setInOutRange({ in: null, out: null })
  }, [selected, reloadComments])

  useEffect(() => {
    if (filteredQueue.length === 0) {
      setSelectedKey(null)
      return
    }
    if (!selectedKey || !filteredQueue.some((item) => reviewItemKey(item) === selectedKey)) {
      setSelectedKey(reviewItemKey(filteredQueue[0]))
    }
  }, [filteredQueue, selectedKey])

  async function runAction(fn: () => Promise<unknown>) {
    setBusy(true)
    setError(null)
    try {
      await fn()
      await reloadQueue()
      if (
        selected &&
        (isReviewShotItem(selected) || (isReviewAssetItem(selected) && reviewMediaKind(selected) === 'video'))
      ) {
        await reloadComments(selected)
      }
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'reviews.actionFailed'))
    } finally {
      setBusy(false)
    }
  }

  function statusLabel(item: ReviewQueueItem): string {
    if (isReviewAssetItem(item)) {
      return t(`assets.status.${item.status}`, { defaultValue: item.status })
    }
    return t(`shots.status.${item.status}`, { defaultValue: item.status })
  }

  function subjectLabel(item: ReviewQueueItem): string {
    return t(`reviews.subject.${reviewItemSubjectType(item)}`, {
      defaultValue: reviewItemSubjectType(item),
    })
  }

  function sourceLink(item: ReviewQueueItem): { to: string; label: string } | null {
    if (isReviewShotItem(item) && item.script_id && item.script_id > 0) {
      return {
        to: `${base}/shots/${item.script_id}/episodes/${item.episode_no ?? 1}`,
        label: t('shots.openEpisodeWorkbench'),
      }
    }
    if (isReviewAssetItem(item)) {
      return { to: `${base}/bible`, label: t('reviews.openBible') }
    }
    return null
  }

  const unresolvedCommentCount = useMemo(
    () => comments.filter((c) => !c.resolved).length,
    [comments],
  )
  const canShowReviewDecision = selected != null
  const canApprove = canShowReviewDecision && unresolvedCommentCount === 0
  const isVideoReview =
    selected != null && reviewMediaKind(selected) === 'video' && Boolean(selected.preview_url?.trim())
  const showAssetNoteOnly =
    selected != null && isReviewAssetItem(selected) && !isVideoReview
  const canUseTimedComments =
    selected != null &&
    (isReviewShotItem(selected) || (isReviewAssetItem(selected) && isVideoReview))

  useEffect(() => {
    if (queue.length === 0) return
    const focus = searchParams.get('focus')
    const tParam = searchParams.get('t')
    const commentParam = searchParams.get('comment')
    if (focus?.includes(':')) {
      const [itemType, itemId] = focus.split(':')
      const match = queue.find((q) => q.item_type === itemType && q.public_id === itemId)
      if (match) setSelectedKey(reviewItemKey(match))
    }
    if (tParam) {
      const ms = Number(tParam)
      if (Number.isFinite(ms) && ms >= 0) pendingSeekMsRef.current = ms
    }
    if (commentParam) setFocusCommentId(commentParam)
  }, [queue, searchParams])

  useEffect(() => {
    if (!isVideoReview || pendingSeekMsRef.current == null) return
    const ms = pendingSeekMsRef.current
    const timer = window.setInterval(() => {
      if (videoPlayerRef.current) {
        videoPlayerRef.current.seekToMs(ms)
        pendingSeekMsRef.current = null
        window.clearInterval(timer)
      }
    }, 80)
    return () => window.clearInterval(timer)
  }, [selected, isVideoReview, comments])

  const commentMarkers = useMemo(
    () =>
      comments
        .filter((c) => c.time_start_ms != null && c.time_end_ms != null)
        .map((c) => ({
          id: c.public_id,
          time_start_ms: c.time_start_ms,
          time_end_ms: c.time_end_ms,
          resolved: c.resolved,
        })),
    [comments],
  )

  const segmentSelectionLabel = useMemo(() => {
    if (!isVideoReview) return null
    const { in: inSec, out: outSec } = inOutRange
    if (inSec != null && outSec != null) {
      return formatReviewTimecodeRange(inSec * 1000, outSec * 1000)
    }
    if (inSec != null) return t('reviews.videoSegmentInOnly', { time: formatReviewTimecode(inSec) })
    if (outSec != null) return t('reviews.videoSegmentOutOnly', { time: formatReviewTimecode(outSec) })
    return null
  }, [inOutRange, isVideoReview, t])

  function commentTimeLabel(c: ReviewComment): string | null {
    if (c.time_start_ms == null || c.time_end_ms == null) return null
    const fps = c.fps_snapshot ?? 24
    return formatReviewTimecodeRange(c.time_start_ms, c.time_end_ms, fps)
  }

  async function toggleCommentResolved(c: ReviewComment, resolved: boolean) {
    if (!selected) return
    setComments((prev) =>
      prev.map((row) => (row.public_id === c.public_id ? { ...row, resolved } : row)),
    )
    if (isReviewShotItem(selected)) {
      await resolveComment(productionId, selected.public_id, c.public_id, resolved)
      return
    }
    if (isReviewAssetItem(selected)) {
      await resolveAssetComment(productionId, selected.public_id, c.public_id, resolved)
    }
  }

  async function submitReviewComment() {
    if (!selected || !commentBody.trim()) return
    const timing = isVideoReview ? videoPlayerRef.current?.getAnnotationRange() ?? undefined : undefined
    if (isReviewShotItem(selected)) {
      await createShotComment(productionId, selected.public_id, commentBody.trim(), timing)
    } else if (isReviewAssetItem(selected)) {
      await createAssetComment(productionId, selected.public_id, commentBody.trim(), timing)
    } else {
      return
    }
    setCommentBody('')
    if (isVideoReview) videoPlayerRef.current?.clearInOut()
    notify('success', t('reviews.commentSent'))
  }

  async function submitApprove() {
    if (!selected) return
    if (isReviewShotItem(selected)) {
      await approveShot(productionId, selected.public_id)
    } else {
      await approveAsset(productionId, selected.public_id)
    }
    notify('success', t('reviews.approveSuccess'))
  }

  function buildRejectFeedback(): string {
    const manual = rejectFeedback.trim()
    if (manual) return manual
    return comments
      .filter((c) => !c.resolved)
      .map((c) => {
        const range = commentTimeLabel(c)
        return range ? `[${range}] ${c.body}` : c.body
      })
      .join('\n')
  }

  async function submitRequestChanges() {
    if (!selected) return
    const feedback = buildRejectFeedback()
    if (!feedback.trim()) return
    if (isReviewShotItem(selected)) {
      await requestShotChanges(productionId, selected.public_id, feedback)
    } else {
      await requestAssetChanges(productionId, selected.public_id, feedback)
    }
    setRejectFeedback('')
    setRejectDialogOpen(false)
    notify('success', t('reviews.requestChangesSuccess'))
  }

  function openRejectDialog() {
    setRejectFeedback('')
    setRejectDialogOpen(true)
  }

  function jumpToComment(c: ReviewComment) {
    setFocusCommentId(c.public_id)
    if (c.time_start_ms != null) videoPlayerRef.current?.seekToMs(c.time_start_ms)
  }

  const handleInOutChange = useCallback((range: { in: number | null; out: number | null }) => {
    setInOutRange(range)
  }, [])

  function renderReviewDecision() {
    if (!canShowReviewDecision) return null
    return (
      <div className="review-workspace__decision shrink-0 border-t border-amber-400/20 px-3 py-3">
        <div className="flex flex-wrap gap-2">
          <ProtoBtn
            size="sm"
            variant="soft"
            tone="emerald"
            disabled={busy || !canApprove}
            onClick={() => void runAction(submitApprove)}
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            {t('reviews.approve')}
          </ProtoBtn>
          <ProtoBtn
            size="sm"
            variant="soft"
            tone="rose"
            disabled={busy}
            onClick={openRejectDialog}
          >
            <XCircle className="h-3.5 w-3.5" />
            {t('reviews.requestChanges')}
          </ProtoBtn>
        </div>
      </div>
    )
  }

  if (loading && queue.length === 0) {
    return (
      <div className="review-workspace flex h-full min-h-0 flex-1 items-center justify-center bg-[#0a0a12]">
        <Loader2 className="h-8 w-8 animate-spin text-amber-400" />
      </div>
    )
  }

  return (
    <div className="review-workspace flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-[#0a0a12]">
      <header className="review-workspace__header shrink-0 border-b border-amber-400/35 bg-[#0b0b13]">
        <div className="flex min-h-11 items-center gap-3 px-4 py-2">
          <Link
            to={base}
            className="inline-flex shrink-0 items-center gap-1.5 text-xs text-zinc-400 transition hover:text-zinc-200"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{t('reviews.backToProduction')}</span>
          </Link>
          <span className="h-4 w-px shrink-0 bg-white/10" aria-hidden />
          <div className="flex min-w-0 shrink-0 items-center gap-2">
            <MessageSquare className="h-4 w-4 shrink-0 text-amber-300" aria-hidden />
            <p className="truncate text-sm font-semibold text-zinc-100">{t('reviews.title')}</p>
          </div>
          <div className="min-w-0 flex-1 overflow-x-auto">
            <SegmentedTabs
              value={filter}
              onChange={setFilter}
              options={FILTER_OPTIONS.map((id) => ({
                value: id,
                label: t(`reviews.filter.${id}`),
              }))}
              aria-label={t('reviews.filterLabel')}
              className="w-max min-w-full justify-end xl:ml-auto xl:justify-end"
            />
          </div>
        </div>
      </header>

      {error ? (
        <p className="shrink-0 border-b border-rose-500/20 bg-rose-500/5 px-4 py-2 text-sm text-rose-300">
          {t(error, { defaultValue: error })}
        </p>
      ) : null}

      <div className="review-workspace__body">
        <aside className="review-workspace__col review-workspace__col--queue">
          <div className="review-workspace__col-head">
            <p className="text-sm font-medium text-zinc-100">
              {t('reviews.queueTitle', { n: filteredQueue.length })}
            </p>
          </div>
          <div className="review-workspace__col-scroll studio-scrollbar-hidden flex flex-col p-2">
            <div className="space-y-1.5">
              {filteredQueue.length === 0 ? (
                <p className="flex min-h-[12rem] flex-1 items-center justify-center px-2 py-8 text-center text-xs text-zinc-600">
                  {t('reviews.empty')}
                </p>
              ) : null}
              {filteredQueue.map((item) => {
                const key = reviewItemKey(item)
                const active = selectedKey === key
                return (
                  <Button
                    key={key}
                    type="button"
                    variant="ghost"
                    onClick={() => setSelectedKey(key)}
                    className={`review-queue-item h-auto w-full items-start justify-start gap-2.5 rounded-xl border p-2.5 text-left ${
                      active
                        ? 'border-amber-400/50 bg-amber-500/10'
                        : 'border-transparent bg-[#12121a] hover:border-amber-400/35'
                    }`}
                  >
                    <div className="relative h-14 w-[4.5rem] shrink-0 overflow-hidden rounded-lg border border-amber-400/35">
                      <ReviewQueueThumb item={item} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="review-queue-item__title text-xs font-medium leading-snug text-zinc-100">
                        {reviewItemTitle(item)}
                      </p>
                      <div className="review-queue-item__meta mt-1.5">
                        <ProtoBadge tone={reviewStatusTone(item)} className="shrink-0 text-[10px]">
                          {statusLabel(item)}
                        </ProtoBadge>
                        <span className="review-queue-item__meta-type text-[10px] text-zinc-600">
                          {subjectLabel(item)}
                        </span>
                      </div>
                      {item.open_comments > 0 ? (
                        <p className="mt-1.5 text-[10px] text-rose-300/90">
                          {t('reviews.openComments', { n: item.open_comments })}
                        </p>
                      ) : null}
                    </div>
                  </Button>
                )
              })}
            </div>
          </div>
        </aside>

        <section className="review-workspace__col review-workspace__col--stage">
          <div className="review-workspace__preview">
            {selected ? (
              <ReviewMediaPreview
                ref={isVideoReview ? videoPlayerRef : undefined}
                item={selected}
                commentMarkers={isVideoReview ? commentMarkers : undefined}
                onCommentMarkerClick={
                  isVideoReview
                    ? (m) => {
                        setFocusCommentId(m.id)
                        if (m.time_start_ms != null) videoPlayerRef.current?.seekToMs(m.time_start_ms)
                      }
                    : undefined
                }
                onInOutChange={isVideoReview ? handleInOutChange : undefined}
              />
            ) : (
              <div className="review-workspace__preview-empty">
                <Film className="h-12 w-12 text-zinc-700" aria-hidden />
                <p className="text-sm text-zinc-500">{t('reviews.emptyPreview')}</p>
              </div>
            )}
          </div>
        </section>

        <aside className="review-workspace__col review-workspace__col--side">
          <div className="review-workspace__col-head review-workspace__side-head">
            <ReviewItemContextBar
              item={selected}
              statusLabel={statusLabel}
              subjectLabel={subjectLabel}
              sourceLink={sourceLink}
            />
            <div className="mt-3 border-t border-amber-400/20 pt-3">
              <p className="text-sm font-medium text-zinc-100">
                {showAssetNoteOnly ? t('reviews.assetNoteTitle') : t('reviews.comments')}
              </p>
            </div>
          </div>

          {showAssetNoteOnly ? (
            <div className="review-workspace__col-scroll px-3 py-3">
              <p className="review-workspace__panel-inset p-3 text-sm leading-relaxed text-zinc-300">
                {selected?.note?.trim() || t('reviews.assetNoteEmpty')}
              </p>
              <p className="mt-3 text-[11px] text-zinc-600">{t('reviews.assetReviewHint')}</p>
              {renderReviewDecision()}
            </div>
          ) : canUseTimedComments ? (
            <>
              <ul className="review-workspace__col-scroll studio-scrollbar-hidden space-y-2 px-3 py-2">
                {selected && comments.length > 0 ? (
                  comments.map((c) => (
                    <li
                      key={c.public_id}
                      className={clsx(
                        'review-workspace__panel-inset p-3 transition',
                        focusCommentId === c.public_id && 'ring-2 ring-amber-400/60',
                      )}
                    >
                      {commentTimeLabel(c) ? (
                        <button
                          type="button"
                          className="mb-1.5 font-mono text-[10px] text-indigo-300/90 hover:text-indigo-200"
                          onClick={() => jumpToComment(c)}
                        >
                          {commentTimeLabel(c)}
                        </button>
                      ) : null}
                      <p
                        className={`text-sm leading-relaxed ${c.resolved ? 'text-zinc-500 line-through' : 'text-zinc-200'}`}
                      >
                        {c.body}
                      </p>
                      <div className="mt-2 flex items-center justify-between gap-2">
                        <span className="inline-flex items-center gap-1 text-[10px] text-zinc-600">
                          <Clock3 className="h-3 w-3" />
                          #{c.author_id}
                        </span>
                        <ProtoBtn
                          size="xs"
                          variant="ghost"
                          disabled={busy}
                          className="text-indigo-300 hover:text-indigo-200"
                          onClick={() => void runAction(() => toggleCommentResolved(c, !c.resolved))}
                        >
                          <RotateCcw className="h-3 w-3" />
                          {c.resolved ? t('reviews.reopen') : t('reviews.resolve')}
                        </ProtoBtn>
                      </div>
                    </li>
                  ))
                ) : (
                  <li className="review-workspace__panel-dashed px-3 py-6 text-center text-xs text-zinc-600">
                    {t('reviews.commentsEmpty')}
                  </li>
                )}
              </ul>
              {renderReviewDecision()}
              <div className="review-workspace__col-foot review-workspace__comment-foot p-3">
                {segmentSelectionLabel ? (
                  <p className="mb-2 font-mono text-[10px] text-indigo-300/90">
                    {inOutRange.in != null && inOutRange.out != null
                      ? t('reviews.videoSegmentReady', { range: segmentSelectionLabel })
                      : segmentSelectionLabel}
                  </p>
                ) : null}
                <div className="flex gap-2">
                  <ProtoInput
                    value={commentBody}
                    onChange={(e) => setCommentBody(e.target.value)}
                    placeholder={
                      isVideoReview && inOutRange.in != null && inOutRange.out != null
                        ? t('reviews.commentSegmentPlaceholder')
                        : t('reviews.commentPlaceholder')
                    }
                    className="min-w-0 flex-1"
                    disabled={busy || !selected || !canUseTimedComments}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey && commentBody.trim() && canUseTimedComments) {
                        e.preventDefault()
                        void runAction(submitReviewComment)
                      }
                    }}
                  />
                  <ProtoBtn
                    size="sm"
                    disabled={busy || !selected || !canUseTimedComments || !commentBody.trim()}
                    onClick={() => void runAction(submitReviewComment)}
                  >
                    {t('reviews.addComment')}
                  </ProtoBtn>
                </div>
              </div>
            </>
          ) : (
            <div className="review-workspace__col-scroll px-3 py-6 text-center text-xs text-zinc-600">
              {t('reviews.commentsEmpty')}
            </div>
          )}
        </aside>
      </div>

      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="max-w-md border-amber-400/35 bg-[#12121a]">
          <DialogHeader>
            <DialogTitle>{t('reviews.requestChanges')}</DialogTitle>
            <DialogDescription className="text-zinc-500">{t('reviews.rejectDialogHint')}</DialogDescription>
          </DialogHeader>
          <ProtoTextarea
            value={rejectFeedback}
            onChange={(e) => setRejectFeedback(e.target.value)}
            placeholder={t('reviews.rejectDialogPlaceholder')}
            rows={4}
            disabled={busy}
            className="min-h-[6rem]"
          />
          <DialogFooter className="gap-2 sm:justify-end">
            <ProtoBtn size="sm" variant="ghost" disabled={busy} onClick={() => setRejectDialogOpen(false)}>
              {t('common.cancel')}
            </ProtoBtn>
            <ProtoBtn
              size="sm"
              variant="soft"
              tone="rose"
              disabled={busy || !buildRejectFeedback().trim()}
              onClick={() => void runAction(submitRequestChanges)}
            >
              {t('reviews.requestChangesConfirm')}
            </ProtoBtn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
